// frontend/src/api/bossBattlesApi.js
// Boss Battles Mode API - Fields of Valhalla
// API endpoints for boss battle league management
//
// ROLLOUT: every endpoint below is gated server-side by the `mode_boss_battle`
// FeatureFlag (display name "Boss Battles"). When the flag does not admit the
// caller the backend answers 404 with
// `{error_code: 'BOSS_BATTLES_FEATURE_DISABLED', message, context}`.
// Use `fetchBossBattlesFeatureFlag()` + `isBossBattlesUiEnabled()` to hide the
// nav entry/route; that is a RENDERING OPTIMIZATION ONLY — the server gate is
// authoritative and callers must still tolerate the 404.
//
// Consumers should read the flag through `useBossBattlesFeatureFlag()`
// (./useBossBattlesFeatureFlag.js) rather than calling the fetch directly, so
// every surface evaluates one identical rule, and should branch on a rejection
// with `isBossBattlesFeatureDisabled(error)` rather than matching messages.

import { API_BASE, getCsrfHeaders } from './transport';
import { handleApiAuthError } from '../auth/authUtils';

// Slug stored in FeatureFlag.name — the exact key the frontend must query.
export const BOSS_BATTLES_FEATURE_FLAG_NAME = 'mode_boss_battle';
export const BOSS_BATTLES_FEATURE_DISABLED_CODE = 'BOSS_BATTLES_FEATURE_DISABLED';

const VALID_FLAG_STATES = new Set(['DISABLED', 'INTERNAL', 'BETA', 'ACTIVE']);

/**
 * Error carrying the rollout gate's `{error_code, message, context}` envelope.
 *
 * Mirrors the `SeasonApiError` / `isSeasonFeatureDisabled` pattern already used
 * by LeagueSignUpPage for the season flag: the UI must be able to tell "this
 * mode is switched off for you" apart from "the request broke", WITHOUT
 * string-matching prose.
 */
export class BossBattlesApiError extends Error {
    constructor(message, { errorCode, status, context = {} }) {
        super(message);
        this.name = 'BossBattlesApiError';
        this.errorCode = errorCode;
        this.status = status;
        this.context = context;
    }
}

/**
 * True when a rejection is the rollout gate refusing the caller.
 *
 * This is an EXPECTED answer for a user the flag does not admit — pages must
 * render an "unavailable" state for it, never a raw error dump.
 *
 * @param {Error} error - Any rejection from this module.
 * @returns {boolean}
 */
export function isBossBattlesFeatureDisabled(error) {
    return Boolean(
        error &&
        error.errorCode === BOSS_BATTLES_FEATURE_DISABLED_CODE
    );
}

/**
 * Inspect a failed response for the gate's 404 envelope.
 *
 * THE 404 IS LOAD-BEARING AND MUST NEVER BE ROUTED THROUGH `handleApiAuthError`:
 * that helper clears cached auth and hard-redirects on 401/403, so treating the
 * gate as an auth failure would LOG THE USER OUT instead of hiding the mode.
 * Callers therefore run this check BEFORE their generic non-OK handling.
 *
 * Resolves to `null` when the response is not the gate envelope (an ordinary
 * 404, or a non-JSON body), so the caller falls through to its own error path
 * rather than mislabelling an unrelated failure as "feature disabled".
 *
 * @returns {Promise<BossBattlesApiError|null>}
 */
function readFeatureDisabledError(response, operation) {
    if (response.status !== 404) {
        return Promise.resolve(null);
    }

    return response.json()
        .then(payload => {
            if (!payload || payload.error_code !== BOSS_BATTLES_FEATURE_DISABLED_CODE) {
                return null;
            }
            console.log(`BossBattlesAPI: ${operation} refused by the mode_boss_battle rollout gate (404 ${BOSS_BATTLES_FEATURE_DISABLED_CODE})`);
            return new BossBattlesApiError(
                typeof payload.message === 'string' && payload.message
                    ? payload.message
                    : 'Boss Battles is not available.',
                {
                    errorCode: BOSS_BATTLES_FEATURE_DISABLED_CODE,
                    status: 404,
                    context: payload.context && typeof payload.context === 'object' ? payload.context : {}
                }
            );
        })
        .catch(() => null);
}

/**
 * Read the `mode_boss_battle` rollout flag (AllowAny endpoint — no credentials required).
 *
 * A 404 is a defined backend response (`{"error": "Flag not found"}`) meaning the
 * flag row does not exist; it maps to an explicit `state: 'DISABLED'` with
 * `exists: false` rather than throwing, because "no flag" is a real rollout
 * state (hide the UI), not a failure. Any other non-OK status throws.
 *
 * @returns {Promise<{name: string, state: string, is_active: boolean, is_internal: boolean, is_beta: boolean, exists: boolean}>}
 */
export function fetchBossBattlesFeatureFlag() {
    return fetch(`${API_BASE}/platform/feature-flags/${BOSS_BATTLES_FEATURE_FLAG_NAME}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Feature flag response status: ${response.status}`);

        if (response.status === 404) {
            console.log('BossBattlesAPI: mode_boss_battle flag not configured (404) - treating as DISABLED');
            return {
                name: BOSS_BATTLES_FEATURE_FLAG_NAME,
                state: 'DISABLED',
                is_active: false,
                is_internal: false,
                is_beta: false,
                exists: false
            };
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to read the mode_boss_battle feature flag`);
        }

        return response.json().then(data => {
            // VALIDATE EXPECTED DATA FORMAT - NO SILENT FAILURES
            if (!data || typeof data.state !== 'string') {
                throw new Error('❌ INVALID RESPONSE: Expected "state" string from the mode_boss_battle feature flag');
            }
            if (typeof data.is_active !== 'boolean' || typeof data.is_internal !== 'boolean' || typeof data.is_beta !== 'boolean') {
                throw new Error('❌ INVALID RESPONSE: Expected boolean "is_active", "is_internal", "is_beta" from the mode_boss_battle feature flag');
            }

            console.log(`BossBattlesAPI: mode_boss_battle flag state=${data.state}`);
            return { ...data, exists: true };
        });
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error reading the mode_boss_battle feature flag:', error);
        throw error;
    });
}

/**
 * Client-side mirror of the server rollout rule in
 * `modes/boss_battles/feature_flag.py::boss_battles_enabled_for`: ACTIVE admits
 * everyone, INTERNAL admits staff only, DISABLED/BETA/unknown fail closed.
 *
 * BETA intentionally hides the UI even though `is_beta` reads true — the backend
 * denies BETA because no cohort rule exists, so never infer availability from
 * `is_beta`.
 *
 * @param {Object} flag - Payload from `fetchBossBattlesFeatureFlag()`.
 * @param {Object} currentUser - Authenticated Django user (needs `is_staff`).
 * @returns {boolean}
 */
export function isBossBattlesUiEnabled(flag, currentUser) {
    if (!flag || typeof flag.state !== 'string' || !VALID_FLAG_STATES.has(flag.state)) {
        return false;
    }
    if (flag.state === 'INTERNAL') {
        return Boolean(currentUser && currentUser.is_staff);
    }
    return flag.state === 'ACTIVE';
}

/**
 * Fetch all available boss battle leagues
 * GET /api/v3/modes/boss_battle/leagues/
 * 
 * Query params (optional):
 * - game_variant: Filter by game variant ID
 * - status: Filter by league status (RECRUITING/LOCKED/RESOLVED/COMPLETED)
 */
export function fetchBossLeagues(filters = {}) {
    let url = `${API_BASE}/v3/modes/boss_battle/leagues/`;
    
    const params = new URLSearchParams();
    if (filters.game_variant) params.append('game_variant', filters.game_variant);
    if (filters.status) params.append('status', filters.status);
    
    if (params.toString()) {
        url += `?${params.toString()}`;
    }
    
    return fetch(url, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch leagues response status: ${response.status}`);

        if (!response.ok) {
            // ROLLOUT GATE FIRST — the 404 envelope must never reach
            // handleApiAuthError (see readFeatureDisabledError).
            return readFeatureDisabledError(response, 'fetchBossLeagues').then(gateError => {
                if (gateError) {
                    throw gateError;
                }

                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }
                throw new Error(`HTTP ${response.status}: Failed to fetch boss leagues`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: Boss leagues retrieved:', data);
        
        // VALIDATE EXPECTED DATA FORMAT - NO FALLBACKS
        if (!Array.isArray(data)) {
            throw new Error('❌ INVALID API RESPONSE: Expected array of leagues');
        }
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching boss leagues:', error);
        throw error;
    });
}

/**
 * Fetch a specific boss battle league
 * GET /api/v3/modes/boss_battle/leagues/{id}/
 */
export function fetchBossLeague(leagueId) {
    // VALIDATE REQUIRED PARAMETER
    if (!leagueId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: leagueId is required');
    }
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/leagues/${leagueId}/`, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch league ${leagueId} response status: ${response.status}`);

        if (!response.ok) {
            // ROLLOUT GATE FIRST. A gated 404 and a genuine "no such league" 404
            // share a status code but are different answers to the user, so the
            // envelope is inspected before either the auth path or the
            // not-found message.
            return readFeatureDisabledError(response, 'fetchBossLeague').then(gateError => {
                if (gateError) {
                    throw gateError;
                }

                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                if (response.status === 404) {
                    throw new Error('Boss league not found');
                }

                throw new Error(`HTTP ${response.status}: Failed to fetch boss league`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: Boss league retrieved:', data);
        
        // VALIDATE EXPECTED DATA FORMAT
        const requiredFields = ['id', 'boss_name', 'status', 'max_participants'];
        requiredFields.forEach(field => {
            if (data[field] === undefined) {
                throw new Error(`❌ INVALID RESPONSE: Missing required field "${field}" in boss league response`);
            }
        });
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching boss league:', error);
        throw error;
    });
}

/**
 * Join a boss battle league
 * POST /api/v3/modes/boss_battle/leagues/{id}/join/
 * 
 * Body: { role: "STRIKER" | "PROTECTOR" | "BUFFER" }
 */
export function joinBossLeague(leagueId, role) {
    // VALIDATE REQUIRED PARAMETERS
    if (!leagueId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: leagueId is required');
    }
    if (!role) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: role is required');
    }
    
    const validRoles = ['STRIKER', 'PROTECTOR', 'BUFFER'];
    if (!validRoles.includes(role)) {
        throw new Error(`❌ INVALID PARAMETER: role must be one of ${validRoles.join(', ')}`);
    }
    
    console.log(`BossBattlesAPI: Joining league ${leagueId} as ${role}`);
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/leagues/${leagueId}/join/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ role })
    })
    .then(response => {
        console.log(`BossBattlesAPI: Join league response status: ${response.status}`);
        
        if (!response.ok) {
            return response.json().then(errorData => {
                console.error('❌ BossBattlesAPI: Join league failed:', errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to join boss league`);
            });
        }
        
        return response.json();
    })
    .then(data => {
        console.log('✅ BossBattlesAPI: Successfully joined boss league:', data);
        
        // VALIDATE EXPECTED RESPONSE
        const requiredFields = ['id', 'league', 'role', 'user'];
        requiredFields.forEach(field => {
            if (data[field] === undefined) {
                throw new Error(`❌ INVALID RESPONSE: Missing required field "${field}" in join response`);
            }
        });
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error joining boss league:', error);
        throw error;
    });
}

/**
 * Fetch participants in a league
 * GET /api/v3/modes/boss_battle/leagues/{id}/participants/
 */
export function fetchLeagueParticipants(leagueId) {
    if (!leagueId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: leagueId is required');
    }
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/leagues/${leagueId}/participants/`, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch participants response status: ${response.status}`);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch league participants`);
        }
        
        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: League participants retrieved:', data);
        
        if (!Array.isArray(data)) {
            throw new Error('❌ INVALID API RESPONSE: Expected array of participants');
        }
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching participants:', error);
        throw error;
    });
}

/**
 * Fetch battle log for a resolved league
 * GET /api/v3/modes/boss_battle/leagues/{id}/battle_log/
 */
export function fetchBattleLog(leagueId) {
    if (!leagueId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: leagueId is required');
    }
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/leagues/${leagueId}/battle_log/`, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch battle log response status: ${response.status}`);
        
        if (response.status === 404) {
            throw new Error('Battle has not been resolved yet');
        }
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch battle log`);
        }
        
        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: Battle log retrieved:', data);
        
        // VALIDATE EXPECTED DATA FORMAT
        const requiredFields = ['id', 'league', 'victory', 'event_log'];
        requiredFields.forEach(field => {
            if (data[field] === undefined) {
                throw new Error(`❌ INVALID RESPONSE: Missing required field "${field}" in battle log response`);
            }
        });
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching battle log:', error);
        throw error;
    });
}

/**
 * Fetch boss heads for a league (after battle starts)
 * GET /api/v3/modes/boss_battle/leagues/{id}/heads/
 */
export function fetchBossHeads(leagueId) {
    if (!leagueId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: leagueId is required');
    }
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/leagues/${leagueId}/heads/`, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch boss heads response status: ${response.status}`);
        
        if (response.status === 404) {
            throw new Error('Boss heads not created yet (battle not started)');
        }
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch boss heads`);
        }
        
        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: Boss heads retrieved:', data);
        
        if (!Array.isArray(data)) {
            throw new Error('❌ INVALID API RESPONSE: Expected array of boss heads');
        }
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching boss heads:', error);
        throw error;
    });
}

/**
 * Set participant loadout (player + gear)
 * POST /api/v3/modes/boss_battle/participants/{id}/set_loadout/
 * 
 * Body: {
 *   player_id: number,
 *   gear_ids: number[],
 *   mythril_spent: number
 * }
 */
export function setParticipantLoadout(participantId, loadoutData) {
    if (!participantId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: participantId is required');
    }
    
    // VALIDATE LOADOUT DATA
    if (!loadoutData.player_id) {
        throw new Error('❌ MISSING REQUIRED FIELD: player_id is required in loadout');
    }
    if (loadoutData.mythril_spent === undefined) {
        throw new Error('❌ MISSING REQUIRED FIELD: mythril_spent is required in loadout');
    }
    
    console.log(`BossBattlesAPI: Setting loadout for participant ${participantId}:`, loadoutData);
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/participants/${participantId}/set_loadout/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(loadoutData)
    })
    .then(response => {
        console.log(`BossBattlesAPI: Set loadout response status: ${response.status}`);
        
        if (!response.ok) {
            return response.json().then(errorData => {
                console.error('❌ BossBattlesAPI: Set loadout failed:', errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to set loadout`);
            });
        }
        
        return response.json();
    })
    .then(data => {
        console.log('✅ BossBattlesAPI: Loadout set successfully:', data);
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error setting loadout:', error);
        throw error;
    });
}

/**
 * Confirm participant loadout
 * POST /api/v3/modes/boss_battle/participants/{id}/confirm_loadout/
 */
export function confirmLoadout(participantId) {
    if (!participantId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: participantId is required');
    }
    
    console.log(`BossBattlesAPI: Confirming loadout for participant ${participantId}`);
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/participants/${participantId}/confirm_loadout/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Confirm loadout response status: ${response.status}`);
        
        if (!response.ok) {
            return response.json().then(errorData => {
                console.error('❌ BossBattlesAPI: Confirm loadout failed:', errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to confirm loadout`);
            });
        }
        
        return response.json();
    })
    .then(data => {
        console.log('✅ BossBattlesAPI: Loadout confirmed successfully:', data);
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error confirming loadout:', error);
        throw error;
    });
}

/**
 * Claim mythril reward after battle
 * POST /api/v3/modes/boss_battle/participants/{id}/claim_reward/
 */
export function claimReward(participantId) {
    if (!participantId) {
        throw new Error('❌ MISSING REQUIRED PARAMETER: participantId is required');
    }
    
    console.log(`BossBattlesAPI: Claiming reward for participant ${participantId}`);
    
    return fetch(`${API_BASE}/v3/modes/boss_battle/participants/${participantId}/claim_reward/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Claim reward response status: ${response.status}`);
        
        if (!response.ok) {
            return response.json().then(errorData => {
                console.error('❌ BossBattlesAPI: Claim reward failed:', errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to claim reward`);
            });
        }
        
        return response.json();
    })
    .then(data => {
        console.log('✅ BossBattlesAPI: Reward claimed successfully:', data);
        
        // VALIDATE EXPECTED RESPONSE
        if (data.mythril_rewarded === undefined) {
            throw new Error('❌ INVALID RESPONSE: Missing mythril_rewarded in claim reward response');
        }
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error claiming reward:', error);
        throw error;
    });
}

/**
 * Fetch available gear pricing for boss battles
 * GET /api/v3/modes/boss_battle/gear_pricing/
 */
export function fetchGearPricing() {
    return fetch(`${API_BASE}/v3/modes/boss_battle/gear_pricing/`, {
        credentials: 'include'
    })
    .then(response => {
        console.log(`BossBattlesAPI: Fetch gear pricing response status: ${response.status}`);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch gear pricing`);
        }
        
        return response.json();
    })
    .then(data => {
        console.log('BossBattlesAPI: Gear pricing retrieved:', data);
        
        if (!Array.isArray(data)) {
            throw new Error('❌ INVALID API RESPONSE: Expected array of gear pricing');
        }
        
        return data;
    })
    .catch(error => {
        console.error('❌ BossBattlesAPI: Error fetching gear pricing:', error);
        throw error;
    });
}
